import api, { LIST, STOREFRONT_ASSETS, STOREFRONT_SECTIONS, STOREFRONT_SETTINGS, STOREFRONT_STYLES } from '../Api'
import { ICollectionResponse } from '../../models/ICollectionResponse';
import {
    IStorefrontAsset,
    IStorefrontAssetActivity,
    IStorefrontAssetScanResult,
    IStorefrontAssetUploadResponse,
    IStorefrontAssetUsage,
} from '../../models/Storefront';
import { IActionResponse } from '../../dal/BaseDAL';
import { ArrayBufferToBase64 } from '../../logic/ArrayBufferConverter';

// Storefront Asset Management: the image library behind every storefront image field.
//
// Scope travels on the doc's `storefront` field (the sections/styles precedent): a storefront id or
// `global`. The list endpoint returns everything the org can see when called without a scope, which
// is what the picker's "All" filter and the management table both want, so the list is cached under
// one LIST tag and every mutation invalidates it.
//
// `replace` is the special one: it rewrites the blob behind a URL that is already stored in
// settings/section/style documents. Nothing about those documents changes server-side, but their
// cached copies are now pointing at different bytes (and may carry a cache-busting URL), so replace
// also invalidates those types wholesale; a tag with no id invalidates every entry of that type.
//
// The per-asset usage and activity lists hang off synthetic ids under the same tag type, so a
// mutation can refresh an asset's audit trail without touching the list.
const usageTag = (id: string) => ({ type: STOREFRONT_ASSETS, id: `usage-${id}` } as const);
const activityTag = (id: string) => ({ type: STOREFRONT_ASSETS, id: `activity-${id}` } as const);

const storefrontAssetApi = api.injectEndpoints({
    endpoints: (build) => ({
        // `undefined` = every asset visible to the org (own storefronts + globals). Pass a
        // storefront id or `global` to narrow server-side.
        getStorefrontAssets: build.query<ICollectionResponse<IStorefrontAsset>, string | undefined>({
            query: (storefront) => storefront
                ? `storefront/assets?storefront=${encodeURIComponent(storefront)}`
                : 'storefront/assets',
            providesTags: (result) => result?.items
                ? [
                    ...result.items.map(a => ({ type: STOREFRONT_ASSETS, id: a.id } as const)),
                    { type: STOREFRONT_ASSETS, id: LIST },
                ]
                : [{ type: STOREFRONT_ASSETS, id: LIST }]
        }),
        getStorefrontAsset: build.query<IStorefrontAsset, string>({
            query: (id) => `storefront/assets/${id}`,
            providesTags: (_result, _error, id) => [{ type: STOREFRONT_ASSETS, id }]
        }),
        // Register a new image. `storefront` is the scope ('global' or a storefront id); `name`
        // defaults server-side to the file name when omitted. `allowAnimated` opts out of the
        // still-image normalisation for banners/GIFs.
        uploadStorefrontAsset: build.mutation<IStorefrontAssetUploadResponse, {
            file: ArrayBuffer,
            fileName: string,
            storefront: string,
            name?: string,
            tags?: string[],
            allowAnimated?: boolean,
        }>({
            query: (arg) => ({
                url: 'storefront/assets/upload',
                method: 'POST',
                body: {
                    file: ArrayBufferToBase64(arg.file),
                    fileName: arg.fileName,
                    storefront: arg.storefront,
                    name: arg.name,
                    tags: arg.tags,
                    allowAnimated: arg.allowAnimated,
                }
            }),
            invalidatesTags: () => [{ type: STOREFRONT_ASSETS, id: LIST }]
        }),
        // Metadata only (name/tags); the blob is untouched, so nothing else needs invalidating.
        updateStorefrontAsset: build.mutation<IActionResponse, { id: string, name?: string, tags?: string[] }>({
            query: (arg) => ({
                url: `storefront/assets/${arg.id}`,
                method: 'PUT',
                body: { name: arg.name, tags: arg.tags }
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: STOREFRONT_ASSETS, id: arg.id },
                { type: STOREFRONT_ASSETS, id: LIST },
                activityTag(arg.id),
            ]
        }),
        // Re-scope a storefront asset to `global`. The id and URL are preserved, so every document
        // already referencing it keeps resolving.
        promoteStorefrontAsset: build.mutation<IActionResponse, { id: string }>({
            query: (arg) => ({
                url: `storefront/assets/${arg.id}/promote`,
                method: 'POST'
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: STOREFRONT_ASSETS, id: arg.id },
                { type: STOREFRONT_ASSETS, id: LIST },
                activityTag(arg.id),
            ]
        }),
        // Swap the bytes behind an existing asset. Every consumer of the URL changes underneath the
        // editor, hence the wholesale settings/sections/styles invalidation.
        replaceStorefrontAsset: build.mutation<IStorefrontAssetUploadResponse, { id: string, file: ArrayBuffer, fileName: string }>({
            query: (arg) => ({
                url: `storefront/assets/${arg.id}/replace`,
                method: 'POST',
                body: {
                    file: ArrayBufferToBase64(arg.file),
                    fileName: arg.fileName,
                }
            }),
            invalidatesTags: (_result, _error, arg) => [
                { type: STOREFRONT_ASSETS, id: arg.id },
                { type: STOREFRONT_ASSETS, id: LIST },
                activityTag(arg.id),
                { type: STOREFRONT_SETTINGS },
                { type: STOREFRONT_SECTIONS },
                { type: STOREFRONT_STYLES },
            ]
        }),
        // Everywhere this asset's URL is referenced. Drives the delete guard and the References tab
        // in the details panel. Expensive server-side (org-wide scan), so callers fetch it lazily.
        getStorefrontAssetUsage: build.query<ICollectionResponse<IStorefrontAssetUsage>, string>({
            query: (id) => `storefront/assets/${id}/usage`,
            providesTags: (_result, _error, id) => [usageTag(id)]
        }),
        // The asset's audit trail: one entry per recorded action (upload, rename, retag, replace,
        // promote, register). Every mutation above invalidates it so the panel stays current.
        getStorefrontAssetActivities: build.query<ICollectionResponse<IStorefrontAssetActivity>, string>({
            query: (id) => `storefront/assets/${id}/activities`,
            providesTags: (_result, _error, id) => [activityTag(id)]
        }),
        // 409 Conflict when the asset is still referenced; the caller surfaces the message.
        deleteStorefrontAsset: build.mutation<IActionResponse, string>({
            query: (id) => ({
                url: `storefront/assets/${id}`,
                method: 'DELETE'
            }),
            invalidatesTags: (_result, _error, id) => [
                { type: STOREFRONT_ASSETS, id },
                { type: STOREFRONT_ASSETS, id: LIST },
            ]
        }),
        // Reconcile storage against the asset library: registers unknown blobs, counts already-known
        // ones and unresolvable (legacy/external) URLs, and reports blobs nothing references.
        scanStorefrontAssets: build.mutation<IStorefrontAssetScanResult, void>({
            query: () => ({
                url: 'storefront/assets/scan',
                method: 'POST'
            }),
            invalidatesTags: () => [{ type: STOREFRONT_ASSETS, id: LIST }]
        }),
        // Permanently deletes the selected blobs from storage.
        cleanupStorefrontAssetOrphans: build.mutation<IActionResponse, { blobPaths: string[] }>({
            query: (arg) => ({
                url: 'storefront/assets/orphans/cleanup',
                method: 'POST',
                body: { blobPaths: arg.blobPaths }
            }),
            invalidatesTags: () => [{ type: STOREFRONT_ASSETS, id: LIST }]
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetStorefrontAssetsQuery,
    useGetStorefrontAssetQuery,
    useUploadStorefrontAssetMutation,
    useUpdateStorefrontAssetMutation,
    usePromoteStorefrontAssetMutation,
    useReplaceStorefrontAssetMutation,
    useGetStorefrontAssetUsageQuery,
    useGetStorefrontAssetActivitiesQuery,
    useDeleteStorefrontAssetMutation,
    useScanStorefrontAssetsMutation,
    useCleanupStorefrontAssetOrphansMutation,
} = storefrontAssetApi
